import {
  ClockPreference,
  DATE_PRESETS,
  DatePreset,
  DateTimeFormatPreference,
  FormatOptions,
  resolveFormats,
} from "./format";

/**
 * The choices the settings UI offers for a date/time preference, plus the
 * sample rendering it previews them with.
 *
 * These live here rather than in the settings component so that the option
 * list, its labels and the preview all move together with the preset table
 * they describe: adding a preset to {@link DatePreset} fails to compile until
 * it is labelled here, and no caller has to reach for `resolveFormats` (or
 * invent its own sample timestamp) just to show the user what a preset looks
 * like. Reached through `useDateTimeFormatPreference`.
 */

/** A selectable date preset and the label shown for it. */
export interface DatePresetOption {
  value: DatePreset;
  label: string;
}

/** A selectable clock convention and the label shown for it. */
export interface ClockOption {
  value: ClockPreference;
  label: string;
}

// Typed as a total record so a new preset can't be added without a label.
const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  long: "Month name",
  system: "System / locale",
  iso: "ISO 8601",
  swiss: "Swiss / European",
  us: "US",
  uk: "UK",
};

/** Date presets in display order, each with its label. */
export const DATE_PRESET_OPTIONS: readonly DatePresetOption[] =
  DATE_PRESETS.map((value) => ({ value, label: DATE_PRESET_LABELS[value] }));

/** Clock conventions in display order, each with its label. */
export const CLOCK_OPTIONS: readonly ClockOption[] = [
  { value: "system", label: "Auto" },
  { value: "12h", label: "12-hour" },
  { value: "24h", label: "24-hour" },
];

/**
 * The timestamp every preview and per-preset example is rendered from:
 * 30 June 2026, 14:05:09.123 local time. Chosen so that day (30) and month
 * (06) can't be mistaken for one another and the hour (14) distinguishes a
 * 12-hour clock from a 24-hour one — the sample reads differently under every
 * preset.
 */
const SAMPLE_DATE = new Date(2026, 5, 30, 14, 5, 9, 123);

/**
 * Render {@link SAMPLE_DATE} under an arbitrary preference — how the settings
 * UI shows what a preset or clock would look like before it is saved. Takes
 * the preference explicitly (rather than reading the active one) because the
 * caller is previewing an unsaved draft.
 */
export function formatSample(
  preference: DateTimeFormatPreference,
  options?: FormatOptions,
): string {
  return resolveFormats(preference).format(SAMPLE_DATE, options);
}
